<template>
  <div class="user-settings-wrapper">
    <h2 class="page-title">{{ $t('userProfileSettings') }}</h2>
    <el-divider></el-divider>

    <el-row :gutter="12" style="margin-bottom: 12px;">
      <el-col :span="24">
        <el-card shadow="never">
          <div class="status-row">
            <el-tag :type="serverMeta.status === 'submitted' ? 'success' : 'info'">
              {{ serverMeta.status === 'submitted' ? $t('upsSubmitted') : $t('upsDraft') }}
            </el-tag>
            <span class="meta-text">{{ $t('upsLastUpdated') }}：<b>{{ displayUpdated }}</b></span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-alert v-if="inlineErr" :title="inlineErrTitle" type="error" show-icon :closable="false" style="margin-bottom:12px">
      <ul v-if="inlineErrList.length" style="margin:4px 0 0 18px;padding:0">
        <li v-for="(e, i) in inlineErrList" :key="i">{{ e }}</li>
      </ul>
    </el-alert>

    <!-- 一般資料 -->
    <el-card class="box-card" style="margin-bottom:12px">
      <div slot="header" class="clearfix">
        <span>{{ $t('upsGeneral') }} <small style="color:#909399">General Information</small></span>
        <el-tag size="mini" type="info" style="float:right">{{ $t('upsLockedNowait') }}</el-tag>
      </div>
      <el-form label-width="160px" size="small">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('upsCompanyName')">
              <el-input v-model="form.companyName" disabled readonly />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="BR">
              <el-input v-model="form.brNumber" placeholder="XXXXXXXX-XXX" disabled readonly />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item :label="$t('contactAddress')">
          <el-input v-model="form.address" type="textarea" :rows="2" disabled readonly />
        </el-form-item>
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('upsServiceFrom')">
              <el-date-picker v-model="form.serviceFrom" type="date" value-format="yyyy-MM-dd" style="width:100%" disabled readonly />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item :label="$t('upsServiceTo')">
              <el-date-picker v-model="form.serviceTo" type="date" value-format="yyyy-MM-dd" style="width:100%" disabled readonly />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- Meta -->
    <el-card class="box-card" style="margin-bottom:12px">
      <div slot="header" class="clearfix">
        <span>Meta Facebook <small style="color:#909399">integration</small></span>
        <el-tag size="mini" type="success" style="float:right">{{ $t('upsMerchantEditable') }}</el-tag>
      </div>
      <el-alert type="warning" :closable="false" style="margin-bottom:12px">
        <span v-html="$t('upsMetaNotice', { email: INVITE_EMAIL })"></span>
        <div style="margin-top:6px">
          <el-button size="mini" @click="copyText(INVITE_EMAIL)">{{ $t('upsCopyEmail') }}</el-button>
          <el-button size="mini" type="text"><a :href="'mailto:' + INVITE_EMAIL + '?subject=NoWait%20Meta%20Business%20Portfolio%20Invitation'">{{ $t('upsSendInvite') }}</a></el-button>
        </div>
      </el-alert>
      <el-form label-width="160px" size="small">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item label="WhatsApp API">
              <el-input v-model="form.waNumber" placeholder="+852 0000 0000" :disabled="!editable('waNumber')" @input="onFieldInput('waNumber')" />
              <div class="hint">{{ $t('upsWaHint') }}</div>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="Company Name" required :class="{ bad: badFields.metaCompanyName }">
              <el-input v-model="form.metaCompanyName" :disabled="!editable('metaCompanyName')" @input="onFieldInput('metaCompanyName')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item label="Tel" required :class="{ bad: badFields.metaTel }">
              <el-input v-model="form.metaTel" placeholder="+852 0000 0000" :disabled="!editable('metaTel')" @input="onFieldInput('metaTel')" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="Business Name" required :class="{ bad: badFields.metaBusinessName }">
              <el-input v-model="form.metaBusinessName" :disabled="!editable('metaBusinessName')" @input="onFieldInput('metaBusinessName')" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- FPS -->
    <el-card class="box-card" style="margin-bottom:12px">
      <div slot="header" class="clearfix">
        <span>FPS {{ $t('upsFpsTitle') }} <small style="color:#909399">FPS API integration</small></span>
        <el-tag size="mini" type="success" style="float:right">{{ $t('upsMerchantEditable') }}</el-tag>
      </div>
      <el-form label-width="160px" size="small">
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item label="Banker" required>
              <el-select v-model="form.fpsBanker" style="width:100%" :disabled="!editable('fpsBanker')" @change="onFieldInput('fpsBanker')">
                <el-option value="" :label="$t('pleaseSelect')"></el-option>
                <el-option v-for="b in banks" :key="b" :value="b" :label="b"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="Account Name" required>
              <el-input v-model="form.fpsAccountName" :disabled="!editable('fpsAccountName')" @input="onFieldInput('fpsAccountName')" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :xs="24" :sm="12">
            <el-form-item label="Bank Code" required>
              <el-input v-model="form.fpsBankCode" inputmode="numeric" placeholder="004" :disabled="!editable('fpsBankCode')" @input="onFieldInput('fpsBankCode')" />
              <div class="hint">3–4 {{ $t('upsDigits') }}</div>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="Account Number" required>
              <el-input v-model="form.fpsAccountNumber" inputmode="numeric" :disabled="!editable('fpsAccountNumber')" @input="onFieldInput('fpsAccountNumber')" />
              <div class="hint warn" v-if="showMaskHint">⚠️ {{ $t('upsMaskHint') }}</div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- ePOS -->
    <el-card class="box-card" style="margin-bottom:12px">
      <div slot="header" class="clearfix">
        <span>ePOS <small style="color:#909399">integration</small></span>
        <el-tag size="mini" type="info" style="float:right">{{ $t('upsLockedNowait') }}</el-tag>
      </div>
      <el-form label-width="160px" size="small">
        <el-form-item label="URL">
          <el-input v-model="form.eposUrl" readonly :disabled="!editable('eposUrl')" style="max-width:520px">
            <el-button slot="append" @click="openEpos">Click</el-button>
          </el-input>
          <el-button size="small" style="margin-left:8px" @click="copyText(form.eposUrl)">Copy</el-button>
          <div class="hint">{{ $t('upsEposHint') }}</div>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- Audit -->
    <el-card class="box-card" style="margin-bottom:80px">
      <div slot="header" class="clearfix" @click="auditOpen = !auditOpen" style="cursor:pointer">
        <span>{{ $t('upsAudit') }} {{ auditOpen ? '▾' : '▸' }}</span>
        <small style="float:right;color:#909399">setting_audit_logs</small>
      </div>
      <div v-show="auditOpen">
        <el-button size="mini" @click="loadAudit" :loading="auditLoading" style="margin-bottom:8px">{{ $t('refresh') }}</el-button>
        <el-table :data="auditList" size="mini" style="width:100%" v-if="auditList.length">
          <el-table-column prop="created_at" :label="$t('time')" width="170">
            <template slot-scope="s">{{ fmtTime(s.row.created_at) }}</template>
          </el-table-column>
          <el-table-column prop="action" label="Action" width="110">
            <template slot-scope="s"><el-tag size="mini">{{ (s.row.action || '').toUpperCase() }}</el-tag></template>
          </el-table-column>
          <el-table-column prop="field" label="Field" width="180" />
          <el-table-column :label="$t('details')">
            <template slot-scope="s"><code class="audit-v">{{ auditVal(s.row) }}</code></template>
          </el-table-column>
        </el-table>
        <div v-else class="hint">{{ online ? $t('upsAuditEmpty') : $t('upsAuditOffline') }}</div>
      </div>
    </el-card>

    <!-- bottom action bar -->
    <div class="actionbar">
      <el-tag v-if="touchedCount" type="warning" size="small">● {{ $t('upsUnsaved') }}</el-tag>
      <span v-else class="hint">{{ barHint || $t('upsBarHint') }}</span>
      <span style="flex:1"></span>
      <el-button size="small" @click="handleReset">{{ $t('upsReset') }}</el-button>
      <el-button size="small" v-if="role === 'nowait' && online" @click="handleResetStatus">{{ $t('upsResetStatus') }}</el-button>
      <el-button size="small" type="primary" @click="openConfirm">{{ $t('upsSubmit') }}</el-button>
    </div>

    <!-- confirm -->
    <el-dialog :title="$t('upsConfirmTitle')" :visible.sync="confirmVisible" :width="$store.state.isMobile ? '90%' : '480px'">
      <p>{{ $t('upsConfirmText') }}</p>
      <ul style="margin:8px 0 0 18px;font-size:13px;color:#606266">
        <li>WhatsApp API：{{ form.waNumber || '—' }}</li>
        <li>Company Name：{{ form.metaCompanyName }}</li>
        <li>Tel：{{ form.metaTel }}</li>
        <li>Business Name：{{ form.metaBusinessName }}</li>
        <li>FPS：{{ form.fpsBanker }} {{ form.fpsBankCode }} {{ form.fpsAccountNumber }}</li>
      </ul>
      <el-progress v-show="submitting" :percentage="submitPct" :show-text="false" style="margin-top:12px" />
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmVisible = false">{{ $t('Cancel') }}</el-button>
        <el-button type="primary" :loading="submitting" @click="confirmSubmit">{{ $t('Sure') }}</el-button>
      </span>
    </el-dialog>

  </div>
</template>

<script>
import {
  getMerchantSettings,
  updateMerchantSettings,
  submitMerchantSettings,
  resetMerchantSettings,
  getMerchantSettingsAudit,
  getSettingsApiBase
} from '@/api/settings'

const FIELDS = [
  ['companyName', 'company_name', 'nowait'],
  ['brNumber', 'br_number', 'nowait'],
  ['address', 'address', 'nowait'],
  ['serviceFrom', 'service_from', 'nowait'],
  ['serviceTo', 'service_to', 'nowait'],
  ['waNumber', 'wa_number', 'nowait'],
  ['metaCompanyName', 'meta_company_name', 'merchant'],
  ['metaTel', 'meta_tel', 'merchant'],
  ['metaBusinessName', 'meta_business_name', 'merchant'],
  ['fpsBanker', 'fps_banker', 'merchant'],
  ['fpsAccountName', 'fps_account_name', 'merchant'],
  ['fpsBankCode', 'fps_bank_code', 'merchant'],
  ['fpsAccountNumber', 'fps_account_number', 'merchant'],
  ['eposUrl', 'epos_url', 'nowait']
]
const TO_DB = Object.fromEntries(FIELDS.map(f => [f[0], f[1]]))
const OWNER = Object.fromEntries(FIELDS.map(f => [f[0], f[2]]))
const ID_OF_DB = Object.fromEntries(FIELDS.map(f => [f[1], f[0]]))

export default {
  name: 'UserProfileSettings',
  data() {
    return {
      INVITE_EMAIL: 'clozeuppautotech@gmail.com',
      banks: [
        'HSBC 滙豐銀行',
        'Hang Seng Bank 恒生銀行',
        'Bank of China (HK) 中國銀行（香港）',
        'Standard Chartered 渣打銀行',
        'DBS 星展銀行（香港）',
        'BOC Hong Kong',
        'ZA Bank 眾安銀行',
        'Mox Bank'
      ],
      merchantId: 'MT-0001',
      role: 'merchant',
      online: false,
      apiBase: getSettingsApiBase(),
      form: { companyName: '', brNumber: '', address: '', serviceFrom: '', serviceTo: '', waNumber: '', metaCompanyName: '', metaTel: '', metaBusinessName: '', fpsBanker: '', fpsAccountName: '', fpsBankCode: '', fpsAccountNumber: '', eposUrl: '' },
      baseline: {},
      touched: {},
      badFields: {},
      serverMeta: { status: 'draft', submitted_at: null, submitted_by_role: null, version: 1, updated_at: null, updated_by_role: null },
      inlineErr: false,
      inlineErrTitle: '',
      inlineErrList: [],
      barHint: '',
      auditOpen: false,
      auditList: [],
      auditLoading: false,
      confirmVisible: false,
      submitting: false,
      submitPct: 0
    }
  },
  computed: {
    touchedCount() { return Object.keys(this.touched).length },
    displayUpdated() {
      if (this.serverMeta.status === 'submitted') return this.fmtTime(this.serverMeta.submitted_at)
      return this.serverMeta.updated_at ? this.fmtTime(this.serverMeta.updated_at) : '—'
    },
    displayUpdatedBy() {
      if (this.serverMeta.status === 'submitted') return this.serverMeta.submitted_by_role
      return this.serverMeta.updated_by_role
    },
    showMaskHint() {
      return this.role === 'merchant' && this.online && String(this.form.fpsAccountNumber || '').includes('*')
    }
  },
  created() {
    this.merchantId = this.resolveMerchantId()
    // 角色按登入權限自動判定：root_management 屬 NoWait 技術人員
    try {
      const auser = JSON.parse(window.localStorage.getItem('auserinfo') || 'null')
      if (auser && auser.root_management === '1') this.role = 'nowait'
    } catch (e) { /* ignore */ }
    this.baseline = Object.assign({}, this.form)
    this.boot()
  },
  methods: {
    resolveMerchantId() {
      try {
        const qs = new URLSearchParams(window.location.search)
        if (qs.get('merchant')) return qs.get('merchant')
        const auser = JSON.parse(window.localStorage.getItem('auserinfo') || 'null')
        if (auser && auser.qyid) return String(auser.qyid)
        const user = JSON.parse(window.localStorage.getItem('userinfo') || 'null')
        if (user && user.id) return String(user.id)
      } catch (e) { /* ignore */ }
      return 'MT-0001'
    },
    lsKey() { return 'nowait.settings.v1.' + this.merchantId },
    metaLsKey() { return 'nowait.settings.meta.v1.' + this.merchantId },
    editable(id) { return this.role === 'nowait' || OWNER[id] === 'merchant' },
    fmtTime(s) { return s ? String(s).replace('T', ' ').slice(0, 16) : '—' },
    roleName(r) { return r === 'nowait' ? this.$t('upsRoleNowait') : (r === 'merchant' ? this.$t('upsRoleMerchant') : '—') },
    onFieldInput(id) {
      if (!this.editable(id)) return
      if (String(this.baseline[id] || '') !== String(this.form[id] || '')) this.$set(this.touched, id, true)
      else this.$delete(this.touched, id)
      this.$delete(this.badFields, id)
      this.inlineErr = false
    },
    async boot() {
      try {
        await this.loadFromServer()
      } catch (e) {
        this.online = false
        if (e && e.code && e.message) {
          // 已連到後端但讀取失敗（如無此用戶）：直接顯示原因，方便定位
          this.barHint = e.message
          this.$message({ type: 'error', message: '讀取失敗：' + e.message })
        } else {
          this.barHint = this.$t('upsOffline')
        }
        try {
          const s = JSON.parse(window.localStorage.getItem(this.lsKey()) || 'null')
          if (s) this.form = Object.assign({}, this.form, s)
          this.baseline = Object.assign({}, this.form)
          const m = JSON.parse(window.localStorage.getItem(this.metaLsKey()) || 'null')
          if (m && m.submitted) {
            this.serverMeta = { status: 'submitted', submitted_at: m.at, submitted_by_role: m.by === 'NoWait 技術人員' ? 'nowait' : 'merchant', version: 1 }
          }
        } catch (err) { /* ignore */ }
      }
    },
    async loadFromServer() {
      const j = await getMerchantSettings(this.merchantId, this.role)
      FIELDS.forEach(([id, db]) => { this.$set(this.form, id, j.data[db] != null ? j.data[db] : '') })
      this.baseline = Object.assign({}, this.form)
      this.touched = {}
      this.serverMeta = { status: j.status, submitted_at: j.submitted_at, submitted_by_role: j.submitted_by_role, version: j.version, updated_at: j.updated_at, updated_by_role: j.updated_by_role }
      this.online = true
    },
    copyText(text) {
      if (!text) { this.$message({ type: 'warning', message: this.$t('upsNothingToCopy') }); return }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => this.$message({ type: 'success', message: this.$t('upsCopied') + text }))
          .catch(() => this.fallbackCopy(text))
      } else this.fallbackCopy(text)
    },
    fallbackCopy(text) {
      const t = document.createElement('textarea')
      t.value = text
      document.body.appendChild(t)
      t.select()
      try { document.execCommand('copy') } catch (e) { /* ignore */ }
      t.remove()
      this.$message({ type: 'success', message: this.$t('upsCopied') + text })
    },
    openEpos() {
      if (this.form.eposUrl) window.open(this.form.eposUrl, '_blank', 'noopener')
      else this.$message({ type: 'warning', message: 'ePOS URL ' + this.$t('upsNotSet') })
    },
    localValidate() {
      const missing = []
      const mark = (id, label) => { this.$set(this.badFields, id, true); missing.push(label) };
      ['metaCompanyName', 'metaTel', 'metaBusinessName', 'fpsBanker', 'fpsAccountName', 'fpsBankCode', 'fpsAccountNumber'].forEach(id => {
        if (!this.editable(id)) return
        if (!String(this.form[id] || '').trim()) mark(id, TO_DB[id])
      })
      if (this.editable('fpsBankCode') && String(this.form.fpsBankCode || '').trim() && !/^\d{3,4}$/.test(String(this.form.fpsBankCode).trim())) {
        this.$set(this.badFields, 'fpsBankCode', true)
        missing.push('fps_bank_code')
      }
      if (this.editable('fpsAccountNumber') && String(this.form.fpsAccountNumber || '').trim().includes('*')) {
        // masked value untouched — skip sending (handled on submit); not an error
      } else if (this.editable('fpsAccountNumber') && String(this.form.fpsAccountNumber || '').trim()) {
        const digits = String(this.form.fpsAccountNumber).replace(/\D/g, '')
        if (digits.length < 6 || digits.length > 20) { this.$set(this.badFields, 'fpsAccountNumber', true); missing.push('fps_account_number') }
      }
      return missing
    },
    openConfirm() {
      this.inlineErr = false
      const missing = this.localValidate()
      if (missing.length) {
        this.inlineErr = true
        this.inlineErrTitle = this.$t('upsFillRequired', { n: missing.length })
        this.inlineErrList = missing
        this.$message({ type: 'error', message: this.$t('upsFillRequired', { n: missing.length }) })
        return
      }
      this.submitPct = 0
      this.confirmVisible = true
    },
    async confirmSubmit() {
      this.submitting = true
      this.submitPct = 35
      if (!this.online) {
        window.localStorage.setItem(this.lsKey(), JSON.stringify(this.form))
        window.localStorage.setItem(this.metaLsKey(), JSON.stringify({ submitted: true, at: this.nowStamp(), by: this.role === 'nowait' ? 'NoWait 技術人員' : '商戶客戶端' }))
        this.submitPct = 100
        setTimeout(() => {
          this.submitting = false
          this.confirmVisible = false
          this.touched = {}
          this.barHint = this.$t('upsOfflineSaved')
          this.$message({ type: 'success', message: this.$t('upsOfflineSaved') })
        }, 400)
        return
      }
      const payload = {}
      Object.keys(this.touched).forEach(id => {
        let v = this.form[id]
        if (id === 'fpsAccountNumber' && String(v || '').includes('*')) return // masked → skip
        payload[TO_DB[id]] = (v === '' ? null : v)
      })
      try {
        if (Object.keys(payload).length) {
          const j = await updateMerchantSettings(this.merchantId, payload, this.role)
          this.applyMeta(j)
          FIELDS.forEach(([id, db]) => { this.baseline[id] = j.data[db] != null ? j.data[db] : '' })
          this.form = Object.assign({}, this.baseline)
          this.touched = {}
        }
        this.submitPct = 70
        const k = await submitMerchantSettings(this.merchantId, this.role)
        this.applyMeta(k)
        this.submitPct = 100
        setTimeout(() => {
          this.submitting = false
          this.confirmVisible = false
          this.barHint = this.$t('upsSubmitOk', { v: this.serverMeta.version })
          this.loadAudit()
          this.$message({ type: 'success', message: this.$t('upsSubmitOk', { v: this.serverMeta.version }) })
        }, 340)
      } catch (err) {
        this.submitting = false
        this.confirmVisible = false
        const code = err && err.code ? String(err.code) : ''
        const detail = (err && err.detail) || {}
        if (code === '-5' && detail.errors) {
          detail.errors.forEach(e => { const id = ID_OF_DB[e.field]; if (id) this.$set(this.badFields, id, true) })
          this.inlineErr = true
          this.inlineErrTitle = detail.msg || 'Error'
          this.inlineErrList = detail.errors.map(e => e.msg)
        } else if (code === '-4') {
          this.inlineErr = true
          this.inlineErrTitle = detail.msg || '無寫入權限'
          this.inlineErrList = detail.rejected_fields || []
        } else {
          this.inlineErr = true
          this.inlineErrTitle = (err && err.message) || 'Error'
          this.inlineErrList = []
        }
        this.$message({ type: 'error', message: this.$t('upsSubmitFail') })
      }
    },
    applyMeta(j) {
      this.serverMeta = { status: j.status, submitted_at: j.submitted_at, submitted_by_role: j.submitted_by_role, version: j.version, updated_at: j.updated_at, updated_by_role: j.updated_by_role }
    },
    handleReset() {
      this.form = Object.assign({}, this.baseline)
      this.touched = {}
      this.badFields = {}
      this.inlineErr = false
      this.barHint = ''
      this.$message({ type: 'info', message: this.$t('upsRestored') })
    },
    async handleResetStatus() {
      if (!this.online) return
      try {
        const j = await resetMerchantSettings(this.merchantId, this.role)
        this.applyMeta(j)
        this.loadAudit()
        this.$message({ type: 'success', message: this.$t('upsResetStatusOk') })
      } catch (err) {
        this.$message({ type: 'error', message: (err && err.message) || 'Error' })
      }
    },
    async loadAudit() {
      if (!this.online) { this.auditList = []; return }
      this.auditLoading = true
      try {
        const r = await getMerchantSettingsAudit(this.merchantId, 12)
        this.auditList = Array.isArray(r) ? r : ((r && r.data) || [])
      } catch (e) {
        this.auditList = []
      }
      this.auditLoading = false
    },
    auditVal(r) {
      if (r.field) return (r.old_value != null ? r.old_value : '—') + ' → ' + (r.new_value != null ? r.new_value : '—')
      return r.new_value || ''
    },
    nowStamp() {
      const d = new Date()
      const p = n => String(n).padStart(2, '0')
      return d.getFullYear() + '-' + p(d.getMonth() + 1) + '-' + p(d.getDate()) + ' ' + p(d.getHours()) + ':' + p(d.getMinutes())
    }
  }
}
</script>

<style lang="scss" scoped>
.user-settings-wrapper {
  .status-row {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
  }
  .meta-text {
    font-size: 12px;
    color: #909399;
    b { color: #606266; }
  }
  .hint {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    &.warn { color: #E6A23C; }
  }
  .bad ::v-deep .el-input__inner {
    border-color: #F56C6C;
    background: #FEF0F0;
  }
  .audit-v {
    font-family: monospace;
    font-size: 11px;
    color: #606266;
  }
  .actionbar {
    position: fixed;
    left: 250px;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,.96);
    border-top: 1px solid #EBEEF5;
    padding: 12px 24px;
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 20;
  }
}
@media screen and (max-width: 768px) {
  .user-settings-wrapper .actionbar { left: 0; }
}
</style>
